import { differenceInDays } from "date-fns";
import { create } from "zustand";

import { browserLocalStorage } from "@/react/lib/browserStorage";

import {
  ECLIPSE_DATE,
  OPT_IN_BAR_DISMISSED_AT_LS_KEY,
  OPT_IN_BAR_ECLIPSE_APPROACH_DAYS,
} from "../constants";

interface OptInBarDismissedValue {
  dismissedAt: string;
}

const isWithinEclipseApproachWindow = (date: Date): boolean =>
  differenceInDays(ECLIPSE_DATE, date) <= OPT_IN_BAR_ECLIPSE_APPROACH_DAYS;

const shouldShowAfterDismiss = (dismissedAt: Date): boolean => {
  if (isWithinEclipseApproachWindow(dismissedAt)) {
    return false;
  }

  return isWithinEclipseApproachWindow(new Date());
};

const readDismissedAtFromStorage = (): Date | null => {
  try {
    const raw = browserLocalStorage.getItem(OPT_IN_BAR_DISMISSED_AT_LS_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as OptInBarDismissedValue;
    if (!parsed.dismissedAt) {
      return null;
    }

    return new Date(parsed.dismissedAt);
  } catch {
    return null;
  }
};

interface OptInBarDismissStore {
  dismiss: () => void;
  dismissedAt: Date | null;
}

export const useOptInBarDismissStore = create<OptInBarDismissStore>(set => ({
  dismissedAt: readDismissedAtFromStorage(),
  dismiss: () => {
    const now = new Date();
    try {
      browserLocalStorage.setItem(
        OPT_IN_BAR_DISMISSED_AT_LS_KEY,
        JSON.stringify({ dismissedAt: now.toISOString() }),
      );
    } catch (error) {
      console.error("LocalStorage is not accessible", error);
    }

    set({ dismissedAt: now });
  },
}));

export const selectIsOptInBarHidden = (dismissedAt: Date | null): boolean =>
  dismissedAt !== null && !shouldShowAfterDismiss(dismissedAt);
